import { getProject } from "./project.selector";
import { createSelector } from "reselect";
import { dockPadEntityMapToList } from "../containers/DockPadFormTemplates";
import { Trade } from "../models/Trade";
import { LookaheadTag, LookaheadTagStatus } from "../models/LookaheadTag";
import { createDeepEqualSelector, isIterable, getLocationPathName, splitFirst } from "./util.selector";
import { Project } from "../models/Project";
import { Milestone } from '../models/Milestone'
import { Lookahead, RowGroup } from "../models/Lookahead";
import moment, { Moment } from 'moment'
import { RootState } from "../modules";
import { ColumnType } from "../components/PhasePlan/Column";
import { isPhasePlanHistogramOpen, getPhasePlanLargestRowsPerColumns, getPhaseConstraints, getPhaseTags, getTrades, getSelectedTags, getSelectedPhasePlan } from "./phaseplan.selector";
import { Tag } from "../models/Tag";
import { Constraint } from "../models/Constraint";
import { TagType, SelectedTag } from "../models/ TagType";
import { PhasePlan } from "../models/PhasePlan";
import { PhaseTag } from "../models/PhaseTag";
import { Point } from "../modules/ui.reducer";
import { getCopiedPhaseTags, getCopiedLookaheadTags, rowCountRow } from "./dialog.selector";
import { getCurrentUserId } from "./user.selector";
import { Commitment, LookaheadCommitmentLog, LookaheadCommitment, CommitmentStatus, CommitmentEntry } from "../actions/lookahead.actions";
import { TagChangeInterface, VarianceTagChangeInterface } from "../actions/phaseplan.actions";
import { HuddleFilter } from "../modules/lookahead.reducer";
import { UndoCommand } from "../models/UndoCommand";
import { Report } from "../models/Report";
import { Area } from "../models/Area";


const _ = require('lodash');

export const getSelectedPhasePlanTags = (state: any): Record<string, SelectedTag> => !!state.phasePlanReducer.selectedTags ? state.phasePlanReducer.selectedTags : {}
const getDropZoneColumn = (state: any, props: any) => props.column;
const getDropZoneIndex = (state: any, props: any) => props.index
const getLocalTrade = (state:any,  props:any):Record<string,Trade> => props.trades

const getLocalRowGroups = (state: any, props: any): Record<string, RowGroup> => {
    if (!!props && !!props.rowGroups) {
        return props.rowGroups
    }

    return {}
}

const getLocalGroupBounds = (state: any, props: any):Record <number, Bound> => {
    if(!props.groupBounds) {
        return {}
    }

    return props.groupBounds
}

const getLocalClosedRowGroups = (state: any, props: any): number[] => {
    if (!!props.closedRowGroups) {
        return props.closedRowGroups
    }

    return []
}


export const getPhasePlans = createSelector([getProject], (project: Project) => {

    if (!project || !project.phases) return {}
    return project.phases
})


export const getSelectedLookaheadId = (state: any) => {
    return state.lookaheadReducer.selectedLookaheadId
}

export const getUndoCommands = (state: any):UndoCommand[] => {
    if(!!state.lookaheadReducer.undoCommands) {
        return state.lookaheadReducer.undoCommands
    }
    return []
}

export const getLookaheadsPhasePlan = createSelector(
    [getPhasePlans, getSelectedLookaheadId],
    (phasePlans: Record<string, PhasePlan>, selectedLookaheadId: string) => {

        const phasePlan = phasePlans[selectedLookaheadId]
        return phasePlan
    })




export const getLookaheadCommitmentLog = createDeepEqualSelector([getProject], (project: Project) => {
    if (!project || !project.lookahead_commitments) return {}
    return project.lookahead_commitments
})

export const getLookaheadCommitments = createDeepEqualSelector([getLookaheadCommitmentLog, getSelectedLookaheadId],
    (commitments: LookaheadCommitmentLog, selectedLookaheadId: string): Record<string, Commitment> | undefined => {
        if (!commitments[selectedLookaheadId]) return {}

        return commitments[selectedLookaheadId].commitments
    })

export const getLookaheadCommitmentList = createDeepEqualSelector([getLookaheadCommitments], (commitments: Record<string, Commitment>): Commitment[] => {
    return dockPadEntityMapToList(commitments)
})

export interface SimpleCommitment {
    name: string,
    uid: string
}

export const getSimpleCommitment = createDeepEqualSelector([getLookaheadCommitmentList], (commitmentList: Commitment[]): SimpleCommitment[] => {
    return commitmentList.map(commitment => ({
        name: commitment.name,
        uid: commitment.uid
    })) as SimpleCommitment[]
})

export const getActiveCommitment = createDeepEqualSelector([getLookaheadCommitmentList], (commitmentList: Commitment[]): Commitment | undefined => {

    const commitmentFilter = commitmentList.filter((commitment: Commitment) => commitment.status === CommitmentStatus.WorkingOn)

    if (commitmentFilter.length <= 0) return undefined

    return commitmentFilter[0]
})



export const getActiveCommitmentLog = createDeepEqualSelector([getActiveCommitment], (commitment: Commitment) => {
    if (!commitment || !commitment.commitLog) {
        return {}
    }

    return commitment.commitLog
})



const getDefaultSelectedCommitmentId = (state: any): Record<string, SelectedTag> => state.lookaheadReducer.selectedCommitmentId


export const getSelectedCommitmentId = createDeepEqualSelector([
    getActiveCommitment,
    getLookaheadCommitmentList,
    getDefaultSelectedCommitmentId],
    (activeCommitment, commitmentList: Commitment[], selectedCommitmentId) => {
        if (!!selectedCommitmentId) {

            return selectedCommitmentId
        }

        if (!!activeCommitment) {

            return activeCommitment.uid
        }

        if (!!commitmentList && commitmentList.length > 0) {
            return commitmentList[0].uid
        }

        return undefined
    })

export const hasCommitments = createDeepEqualSelector([getLookaheadCommitmentList], (commitmentList: Commitment[]) => {
    if (!commitmentList || commitmentList.length <= 0) {
        return false
    }

    return true
})

export const getActiveCompeleteCommitmentLog = createDeepEqualSelector([getActiveCommitmentLog],
    (commitLog: Record<string, CommitmentEntry>) => {
        const commitLogList = dockPadEntityMapToList(commitLog)

        return commitLogList.filter((log: CommitmentEntry) => !!log.actualDate && !!log.variance)
    })



export const hasCommitment = createDeepEqualSelector([getLookaheadCommitmentList], (commitmentList: Commitment[]) => {

    const commitmentFilter = commitmentList.filter((commitment: Commitment) => commitment.status === CommitmentStatus.WorkingOn)

    return commitmentFilter.length > 0
})



export const getLookaheads = createDeepEqualSelector([getProject], (project: Project) => {

    if (!project || !project.lookaheads) return {}
    return project.lookaheads
})

export const hasLookaheadPlans = createDeepEqualSelector([getLookaheads], (lookaheads: Record<string, PhasePlan>) => {
    
    if(!lookaheads) {
        return false
    }

    return Object.keys(lookaheads).length > 0
})

export const getSelectedLookahead = createDeepEqualSelector(
    [getLookaheads, getSelectedLookaheadId],
    (lookaheads: Record<string, Lookahead>, selectedLookaheadId: string) => {
        const lookahead = lookaheads[selectedLookaheadId]
        return lookahead
    })



    export const getLookaheadTrades = (state: any): Record<string, Trade> => state.lookaheadReducer.trades



export const getForceRefreshColumns = (state:any): string[] => state.lookaheadReducer.forceRefreshColumns

export const getLookaheadTradesList = createSelector([getLookaheadTrades], (trades: Record<string, Trade>) =>
    dockPadEntityMapToList(trades))

export const getLookaheadTradeSelectList = createSelector([getLookaheadTradesList], (tradesList) => {
    return tradesList.map(trade => {
        return {
            uid: trade.uid,
            name: trade.name
        }
    })
})



export type LookaheadNameKey = {
    name: string,
    key: string
}

export const getLookaheadNames = createDeepEqualSelector([getLookaheads], (lookaheads?: Record<string, Lookahead>): LookaheadNameKey[] => {
    if (!lookaheads) {
        return []
    }

    return Object.entries(lookaheads).filter(([, lookahead]) => !!lookahead.name).map(([key, lookahead]) => {
        return {
            name: lookahead.name,
            uid: key
        }
    })
})


export const getLookaheadTags = (state: any) => state.lookaheadReducer.tags

export const getLookaheadMilestoneTags =  (state: any) => state.lookaheadReducer.milestoneTags

export const getLookaheadConstraints = (state: any) =>  state.lookaheadReducer.constraints

export const getAreas = (state: any): Record<string, Area> =>  state.lookaheadReducer.areas

export const getDownloadUrl = (state: any, ownProps?: any) => {
}

export const getRowGroups = (state: any):  Record<string, RowGroup> =>  state.lookaheadReducer.row_groups

export const getSelectedCommitment = createDeepEqualSelector(
    [(state: any) =>
        _.get(state, 'firebase.data.SELECTED_COMMITMENT')], (commitment: Commitment, state) => {

            if (!commitment) {
                return null
            }

            return commitment
        })

export const getDailyReports = createDeepEqualSelector(
    [(state: any):Record<string, Report> =>
        _.get(state, 'firebase.data.SELECTED_DAILYREPORTS')], (reports: any) => {
            if (!reports) {
                return null
            }

            return reports
        })

export const getSelectedCommitmentLog = createDeepEqualSelector([getSelectedCommitment], (commitment: Commitment) => {
    if (!commitment || !commitment.commitLog) {
        return []
    }


    const commitLogList = dockPadEntityMapToList(commitment.commitLog)


    return commitLogList
})

export type TradePPC = {
    tradeId: string,
    tradeColor: string,
    todoCount: number,
    finishedCount: number,
    ppc: number,
    tradeName: string
}


export const getPPCPerTrade = createDeepEqualSelector([getSelectedCommitment, getLookaheadTags, getLookaheadTrades], (selectedCommitment: Commitment, tags: Record<string, LookaheadTag>, trades: Record<string, Trade>) => {
    if (!selectedCommitment) {
        return []
    }

    const commitLog = selectedCommitment.commitLog
    const tradePPCs: Record<string, TradePPC> = {}
    Object.entries(commitLog).forEach(([key, value]) => {

        if (!!tags[key] && !!tags[key].tradeId && !!trades[tags[key].tradeId]) {
            const tag = tags[key]
            const trade = trades[tag.tradeId]
            if (!tradePPCs[tag.tradeId]) {
                tradePPCs[tag.tradeId] = {
                    tradeId: tag.tradeId,
                    tradeName: trade.name,
                    tradeColor: trade.color,
                    todoCount: 0,
                    finishedCount: 0
                }
            }

            if (!value.originalStatus || !LookaheadTagStatus[value.originalStatus] || value.originalStatus === LookaheadTagStatus.Todo || tag.status === LookaheadTagStatus.WorkingOn) {
                const todoCount = tradePPCs[tag.tradeId].todoCount + 1
                let finishedCount = 0
                if (value.actualStatus == 'DoneDone' || value.actualStatus == LookaheadTagStatus.Done) {
                    finishedCount = tradePPCs[tag.tradeId].finishedCount + 1
                } else {
                    finishedCount = tradePPCs[tag.tradeId].finishedCount
                }

                const ppc = Math.trunc((finishedCount / todoCount) * 100)
                tradePPCs[tag.tradeId].ppc = ppc
                tradePPCs[tag.tradeId].finishedCount = finishedCount
                tradePPCs[tag.tradeId].todoCount = todoCount

            }

        }
    })


    let totalTodoCount = 0
    let totalFinishCount = 0

    Object.entries(tradePPCs).forEach(([key, value]) => {
        totalTodoCount += value.todoCount
        totalFinishCount += value.finishedCount
    })


    tradePPCs['Overall'] = {
        tradeId: 'Overall',
        tradeColor: '#4286dd',
        tradeName: 'Overall',
        todoCount: totalTodoCount,
        finishedCount: totalFinishCount,
        ppc: Math.trunc((totalFinishCount / totalTodoCount) * 100)
    }


    return tradePPCs
})

export const getLookaheadMilestones = createSelector([getSelectedLookahead], (lookahead) => {
    if (!lookahead || !lookahead.locations) return {}

    return lookahead.locations
})

export const getMileStoneByLocationId = (locationId: string, locationById: Record<string, Milestone>, locationByName: Record<string, Milestone>) => {
    if (!!locationById[locationId]) {
        return locationById[locationId]
    }

    if (!!locationByName[locationId]) {
        return locationByName[locationId]
    }

    return null
}

function mod(n: number, m: number) {
    return ((n % m) + m) % m;
}

export const getLookaheadTagsList = createSelector([getLookaheadTags], (tags) => {

    return dockPadEntityMapToList(tags).map((tag: LookaheadTag) => {
        tag.tagCardType = TagType.Lookahead
        return tag
    })
})

export const getTradeFilter = (state: any) => state.lookaheadReducer.tradeFilter;

export enum  NewTagType {
    Lookahead='LookaheadTag',
    Constraint='ConstraintTag',
    Milestone='MilestoneTag',
} 
export type TagRecord  = {
    uid: string;
    indexId: number;
    tradeId: string;
    name: string;
    tagDate: string;
    type: NewTagType;
}

export const getAllLookaheadTags = createSelector([ getLookaheadTags, getLookaheadConstraints, getLookaheadMilestoneTags],
    ( tags: Record<string, LookaheadTag>,  constraints: Record<string, Constraint>,  milestoneTags: Record<string, LookaheadTag>) => {
        return {...tags, ...milestoneTags,...constraints}
})


export const getLookaheadColumnTags = (state: any) => {

    return state.lookaheadReducer.tagsByColumn
}

export const getTagRecord = (state: any, props: any): TagRecord => props.tagRecord

export const getTagFromRecord = createSelector([getTagRecord, getLookaheadTags, getLookaheadConstraints, getLookaheadMilestoneTags], (tagRecord: TagRecord, tags: Record<string, LookaheadTag>,  constraints: Record<string, Constraint>, milestoneTags: Record<string, LookaheadTag>) => {
    if(!!tags[tagRecord.uid]) {
        return tags[tagRecord.uid]
    }
_
    if(!!constraints[tagRecord.uid]) {
        return constraints[tagRecord.uid]
    }

    if(!!milestoneTags[tagRecord.uid]) {
        return milestoneTags[tagRecord.uid]
    }
})

const getTagUid = (state: any, props: any) => props.uid;
const getAreaUid = (state: any, props: any) => props.uid;


const getSimpleTagData = (tag: any) => {
    return {
        uid: tag.uid,
        name: tag.name,
        tagCardType: tag.tagCardType,
        tradeId: tag.tradeId,
        tagDate: tag.tagDate,
        status: tag.status,
        crewSize: tag.crewSize,
        locationId: tag.locationId,
    };
}

const getConstraintTagData = (tag: any) => {
    return {
        uid: tag.uid,
        description: tag.description,
        tagCardType: tag.tagCardType,
        tagDate: tag.tagDate,
        status: tag.status,
    };
}

const getMilestoneTagData = (tag: any) => {
    return {
        uid: tag.uid,
        name: tag.name,
        tagCardType: tag.tagCardType,
        isMilestone: tag.isMilestone,
        tradeId: tag.tradeId,
        tagDate: tag.tagDate,
        status: tag.status,
        crewSize: tag.crewSize,
        locationId: tag.locationId,
    };
}

export const makeGetTagData = () => {
    return createSelector(
        [getTagUid, getLookaheadTags, getLookaheadConstraints, getLookaheadMilestoneTags],
        (uid: string, tags: Record<string, LookaheadTag>,  constraints: Record<string, Constraint>, milestoneTags: Record<string, LookaheadTag>) => {

            if(!!tags[uid]) return getSimpleTagData(tags[uid]);

            if(!!constraints[uid]) return getConstraintTagData(constraints[uid]);

            if(!!milestoneTags[uid]) return getMilestoneTagData(milestoneTags[uid]);

            return null;
        }
    );
}

export const makeGetAreaData = () => {
    return createSelector(
        [getAreaUid, getAreas],
        (uid: string, areas: Record<string, Area>) => {

            if(!!areas[uid]) return areas[uid];

            return null;
        }
    );
}

export const getLookaheadOffDays = createDeepEqualSelector([getSelectedLookahead], (selectedLookahead: Lookahead) => {
    return (!!selectedLookahead && !!selectedLookahead.offDays) ? (new Set<number>(selectedLookahead.offDays.map(i => mod(i + 1, 7)))) : new Set()
})

export const getLookaheadName = createDeepEqualSelector([getSelectedLookahead], (selectedLookahead: Lookahead) => {
    return !!selectedLookahead ? selectedLookahead.name : ""
})

export const isLockedDown = createDeepEqualSelector([getSelectedLookahead], (selectedLookahead: Lookahead) => {
    if (!selectedLookahead) {
        return false
    }
    return !!selectedLookahead.isLockedDown
})



export const getLocalRowGroupStarts = createSelector([getLocalRowGroups], (rowGroups): number[] => {
    return Object.keys(rowGroups).map(key => {
        const rowGroup = rowGroups[key]

        return parseInt(rowGroup.start)
    })
})

export const getRowGroupStarts = createSelector([getRowGroups], (rowGroups): number[] => {
    return Object.keys(rowGroups).map(key => {
        const rowGroup = rowGroups[key]

        return parseInt(rowGroup.start)
    })
})




export const getRowGroupEnds = createSelector([getRowGroups], (rowGroups): number[] => {
    return Object.keys(rowGroups).map(key => {
        const rowGroup = rowGroups[key]

        return parseInt(rowGroup.end)
    })
})

export const getRowGroupMaxEnds = createSelector([getRowGroupEnds, getLookaheadMilestoneTags, getLookaheadTags], (rowGroups, milestones, tags): number => {
    const tagIndexs = !!tags ? Object.keys(tags).filter(key => tags[key].indexId != undefined && tags[key].indexId != null).map(key => tags[key].indexId) : []

    const milestoneIndexs = !!milestones ? Object.keys(milestones).filter(key => milestones[key].indexId != undefined && milestones[key].indexId != null).map(key => milestones[key].indexId) : []

    const maxRow = _.max([...rowGroups, ...tagIndexs, ...milestoneIndexs])
    return !!maxRow ? maxRow : 0
})



export const getCommitmentDay = createDeepEqualSelector([getSelectedLookahead], (selectedLookahead: Lookahead) => {
    if (!selectedLookahead) {
        return null
    }
    return selectedLookahead.commitment_day
})



export const getLookaheadLargestRowsPerColumns = createDeepEqualSelector([getLookaheadColumnTags], (tagsByColumn: Record<any, any[]>) => {
    return Object.values(tagsByColumn).reduce((obj, cur) => {
        if (!!cur) {
            obj += cur.length
        }

        return obj
    }, 1)
})




const getTagSelectName = (tag: LookaheadTag) => {
    if (!tag.tagDate) {
        return `${tag.name} - Backlog`
    } else {
        return `${tag.name} - ${tag.tagDate}`
    }
}

export const getLookaheadTagSelectList = createSelector([getLookaheadTagsList], (tagList) => {
    return tagList.map(tag => {
        return {
            uid: tag.uid,
            name: getTagSelectName(tag)
        }
    })

})

export const getLookaheadTagLocationsSelectList = createSelector([getLookaheadTagsList], (tagList) => {
    return Object.keys(_.groupBy(tagList, 'locationId')).map(locationId => {
        return {
            uid: locationId,
            name: locationId
        }
    })
})

export const getLookaheadTagId = (state: any) => state.lookaheadReducer.lookaheadTagToEdit

export const isNewLookaheadTag = createSelector([getLookaheadTagId], (tagId) => {
    if (!!tagId) {
        return false
    }

    return true

})

export const getSelectedRows = (state: any) => state.lookaheadReducer.selectedRows
export const getSelectedColumns = (state: any) => state.lookaheadReducer.selectedColumns


export const getTagDate = (state: any) => state.lookaheadReducer.tagDate

export const getRow = (state: any) => state.lookaheadReducer.row

export const getHuddleFilter = (state: any) => state.lookaheadReducer.huddleFilter

export const getHuddleDate = (state:any) => moment(state.lookaheadReducer.huddleDate, 'l')

export const isReportDialogOpen = (state:any) => state.lookaheadReducer.isReportDialogOpen

export const getReportViewTradeId = (state:any) => state.lookaheadReducer.reportViewTradeId

export const getReportViewDate = (state:any) => state.lookaheadReducer.reportViewDate


export const getSelectedLookaheadTags = (state: any): Record<string, SelectedTag> => state.lookaheadReducer.selectedTags

export const isLookaheadMilestone = (state: any): Record<string, SelectedTag> => state.lookaheadReducer.isMilestone


export const getReportData = createSelector([getDailyReports, getReportViewTradeId], (reports: Record<string, Report>, tradeId) => {

    if(!reports || !reports[tradeId]) {
        return null
    }

    return reports[tradeId]
})

export const getLookaheadTag = createSelector(
    [getLookaheadTags, getLookaheadMilestoneTags, getLookaheadTagId, isLookaheadMilestone], (tags, milestoneTags, tagId, isMilestone) => {
        if (!tagId || !tags) {
            return null
        }

        const combinedTags = { ...tags, ...milestoneTags }

        return combinedTags[tagId]
    })


const selectTags = (state: any, props: any) => props.tags;

export const selectTag = (state: any, props: any) => props.tag;

const selectTrades = (state: any, props: any) => props.trades;

export const selectType = (state: any, props: any) => props.type;

export const selectColumn = (state: any, props: any): number => props.column;
export const getTagByColumnDate = (state:any, props:any):  Record<string, LookaheadTag[]> => props.tagByColumn

export const getLookaheadColumnTagsByColumn = createDeepEqualSelector([selectColumn, getLookaheadColumnTags, getTradeFilter],(column, tagsByColumn, tradeFilter): TagRecord[] => {
    const columnString = moment(column).format('l')

    if(!tagsByColumn || !tagsByColumn[columnString]) {
        return []
    }

    const tags =  tagsByColumn[columnString]

    if(!tags) {
        return []
    }

    if(!tradeFilter) {
        return tags
    }

    return tags.filter(tag => tag.tradeId == tradeFilter.uid)
})

export const isToday = createDeepEqualSelector([selectColumn], (column) => {
    return moment().startOf('day').isSame(moment(column).startOf('day'))
})
export const makeIsToday = () => {
    return createSelector([selectColumn], (column) => {
        return moment().startOf('day').isSame(moment(column).startOf('day'))
    });
};

export const getTagsByIndex = createDeepEqualSelector([getLookaheadColumnTagsByColumn], (tags: TagRecord[]) => {
    return _.groupBy(tags, 'indexId')
})



export const getMaxIndex = createDeepEqualSelector([getLookaheadTags, getLookaheadConstraints], (tags: Record<string, LookaheadTag>, constraints: Record<string, Constraint>) => {
    const allTags = { ...tags, ...constraints }
    const allTagsList = dockPadEntityMapToList(allTags)
    const maxIndexIdTag = _.maxBy(allTagsList, 'indexId')
    if (!maxIndexIdTag) {
        return 20
    }

    return Math.max(maxIndexIdTag.indexId, 20)
})
export const getCrewSize = createDeepEqualSelector([getLookaheadColumnTagsByColumn, getAllLookaheadTags], (tagRecords: TagRecord[], tags: Record<string, Tag>): number => {
    return tagRecords.reduce((sum: number, tagRecord: TagRecord) => {
        const tag = tags[tagRecord.uid]
        const crewSize = (!!tag && !!tag.crewSize) ? tag.crewSize : 0
        return sum + crewSize
    }, 0)
})


export const isTagSelected = createSelector([getTagFromRecord, getSelectedLookaheadTags], (tag,  lookaheadTags: Record<string, SelectedTag>) => {
    if(!tag) {
        return
    }

    const tagId = tag.uid
    let tagSet: Record<string, SelectedTag> | null = lookaheadTags


    if (!tagSet) return false

    return !!tagSet[tagId]
})

export const makeIsTagSelected = () => {
    return createSelector([getTagUid, getSelectedLookaheadTags], (uid: string,  lookaheadTags: Record<string, SelectedTag>) => {
        if(!uid) {
            return
        }
    
        const tagId = uid
        let tagSet: Record<string, SelectedTag> | null = lookaheadTags
    
    
        if (!tagSet) return false
    
        return !!tagSet[tagId]
    });
}

export const countTagSelected = createSelector([selectType, getSelectedLookaheadTags, getSelectedPhasePlanTags, isTagSelected], (type, lookaheadTags, phaseplanTags: Record<string, SelectedTag>, isTagSelected) => {


    let tags: Record<string, SelectedTag> | null = null

    if (type === ColumnType.Lookahead) {
        tags = lookaheadTags
    }

    if (type === ColumnType.PhasePlan) {
        tags = phaseplanTags
    }

    if (!tags) return 0

    if (!isTagSelected) return 0


    return Object.keys(tags).length
})


export const getSelectedTagSet = createDeepEqualSelector([getLocationPathName, getSelectedLookaheadTags, getSelectedPhasePlanTags], (pathName, lookaheaTags, phasePlanTags): Record<string, SelectedTag> => {


    let tagSet = null

    if (pathName === '/lookahead') {
        tagSet = { ...lookaheaTags }
    }

    if (pathName === '/phasePlan') {
        tagSet = { ...phasePlanTags }
    }

    if (!tagSet) return {}

    return tagSet
})

export const getMilestoneKeys = createDeepEqualSelector([getLookaheadMilestoneTags], (milestones) => {
    if (!milestones) {
        return new Set<String>()
    }

    return new Set<String>(Object.keys(milestones))
})

export const getSelectedLookaheadColumn = (state: any): Point => state.uiReducer.lookaheadColumnSelected

export const getClosedRowGroups = (state: any): number[] => state.lookaheadReducer.closedRowGroups




const getDropZoneType = (state: any, props: any) => props.type
export const getSelectedPhasePlanColumn = (state: any): Point => state.uiReducer.phasePlanColumnSelected

export const isDropZoneSelected = createDeepEqualSelector(
    [
        getDropZoneColumn,
        getDropZoneIndex,
        getDropZoneType,
        getSelectedLookaheadColumn,
        getSelectedPhasePlanColumn
    ],
    (column, index, type, lookaheadColumn, phasePlanColumn) => {

        let selectedColumn: Point | null = null

        if (type === ColumnType.Lookahead) {
            selectedColumn = lookaheadColumn
        }

        if (type === ColumnType.PhasePlan) {
            selectedColumn = phasePlanColumn
        }

        if (!selectedColumn || !column) return false

        return selectedColumn.column === column && selectedColumn.row === index
    })

export const isLookaheadHistogramOpen = (state: any) => {
    const { isHistogramOpen } = state.lookaheadReducer


    if (!isHistogramOpen) return false


    return isHistogramOpen
}

export const isHistogramOpen = (state: any, ownProps?: any) => {
    if (!ownProps) return false

    const { type } = ownProps

    if (!type) return false

    let _isHistogramOpen = false

    if (type === ColumnType.Lookahead) {
        _isHistogramOpen = isLookaheadHistogramOpen(state)

    }

    if (type === ColumnType.PhasePlan) {
        _isHistogramOpen = isPhasePlanHistogramOpen(state)

    }

    return _isHistogramOpen
}

export interface TradeAndCrew {
    name: string,
    crewSize: number
}




export const getTradesByCrewsize = createDeepEqualSelector([getLookaheadColumnTagsByColumn, selectTrades], (tags, trades) => {

    if ( !trades) return false

    const tagsByTradeId = _.groupBy(tags, 'tradeId')
    const tagsByTrade = Object.keys(tagsByTradeId)
        .filter((tradeId: string) => {
            return !!trades[tradeId]
        }).reduce((obj, tradeId: string) => {
            const tagsBy = tagsByTradeId[tradeId]

            const crewSize = _.sumBy(tagsBy, 'crewSize')

            obj[tradeId] = {
                name: trades[tradeId].name,
                crewSize
            }

            return obj
        }, {} as Record<string, TradeAndCrew>)

    return tagsByTrade
})

export const getCrewSizeData = createDeepEqualSelector([getTagByColumnDate, getLocalTrade], (tagByColumn: Record<string, LookaheadTag[]>, trades: Record<string, Trade>): Record<string, Record<string, TradeAndCrew> > => {
    return Object.keys(tagByColumn).reduce((mainObj, columnId) => {
        const tags = tagByColumn[columnId]

        const tagsByTradeId = _.groupBy(tags, 'tradeId')
        
        const tagsByTrade = getTagsByTradeId(tagsByTradeId, trades)
        
        mainObj[columnId] = tagsByTrade
        
        return mainObj
    }, {} as Record<string, Record<string, TradeAndCrew>>)

})

export const getPhasePlanTags = (state: any) => (getPhaseTags(state))

export const getPhasePlanTrades = (state: any) => (getTrades(state))

export const getTradeColorByTags = createDeepEqualSelector(
    [
        getPhasePlanTags, getLookaheadTrades, getPhasePlanTrades, getLocationPathName
    ], (phasePlanTags: Record<string, PhaseTag>, lookaheadTrades: Record<string, Trade>, phasePlanTrades: Record<string, Trade>, pathName: string) => {

        const colorByTagID = {} as Record<string, string>

        if (pathName === '/lookahead') {
            for (let tagId in phasePlanTags) {
                if (!colorByTagID[tagId] && !!lookaheadTrades[phasePlanTags[tagId].tradeId]) {
                    colorByTagID[tagId] = lookaheadTrades[phasePlanTags[tagId].tradeId].color
                }
            }
        }

        if (pathName === '/phasePlan') {
            for (let tagId in phasePlanTags) {
                if (!colorByTagID[tagId] && !!phasePlanTrades[phasePlanTags[tagId].tradeId]) {
                    colorByTagID[tagId] = phasePlanTrades[phasePlanTags[tagId].tradeId].color
                }
            }
        }
        return colorByTagID
    })


export const getDraggedId = (state: any): string => state.uiReducer.draggedId

export const getDraggedOldPoint = (state: any): Point => state.uiReducer.draggedOldPoint
export const getDraggedNewPoint = (state: any): Point => state.uiReducer.draggedNewPoint
export const getSelectedNewPoints = (state: any): Array<Point> => state.uiReducer.selectedNewPoints

export const getTopLeftSelected = (selectedTag: Record<string, SelectedTag>): SelectedTag | null => {
    if (Object.keys(selectedTag).length <= 0) return

    let minColumnArray: SelectedTag[] = []
    let minColumn: number | null = null

    Object.values(selectedTag).filter((tag) => !!tag && !!tag.position && !!tag.position.column).forEach(tag => {


        if (!minColumn) {
            minColumn = tag.position.column
            minColumnArray.push(tag)
        }

        if (tag.position.column < minColumn) {
            minColumnArray = []
            minColumnArray.push(tag)
            minColumn = tag.position.column
        }

        if (tag.position.column === minColumn) {
            minColumnArray.push(tag)
        }
    })


    let minRow: number | null = null

    let minRowObject = null


    minColumnArray.forEach(tag => {
        if (!minRow) {
            minRow = tag.position.row
            minRowObject = tag
        }

        if (tag.position.row < minRow) {
            minRow = tag.position.row
            minRowObject = tag
        }
    })


    return minRowObject
}

export const isPendingPasteDropZone = createDeepEqualSelector(
    [
        getDropZoneColumn,
        getDropZoneIndex,
        getDropZoneType,
        getLocationPathName,
        getSelectedLookaheadColumn,
        getSelectedPhasePlanColumn,
        getCopiedPhaseTags,
        getCopiedLookaheadTags
    ], (
        dropZoneColumn: number,
        dropZoneIndex: number,
        dropZoneType: any,
        pathName: string,
        lookaheadColumn: Point,
        phasePlanColumn: Point,
        phasePlanTags: Record<string, SelectedTag>,
        lookaheadTags: Record<string, SelectedTag>): boolean => {

    let selectedPoint
    let selectedTags: Record<string, SelectedTag>
    if (pathName === '/phasePlan') {
        selectedPoint = phasePlanColumn
        selectedTags = phasePlanTags
    }

    if (pathName === '/lookahead') {
        selectedPoint = lookaheadColumn
        selectedTags = lookaheadTags
    }


    if (!selectedPoint || !selectedTags) return false

    // Get first tag
    if (Object.keys(selectedTags).length <= 0) return false

    const firstTag = getTopLeftSelected(selectedTags)

    // Get the tag column and index
    const oldPosition = firstTag.position

    const oldColumnId = oldPosition.column
    const oldIndexId = oldPosition.row

    const newColumnId = selectedPoint.column
    const newIndexId = selectedPoint.row

    //Calculate the column offset and index offset new - old = offset
    let offsetColumn: number
    let offsetIndex: number

    const newColumnMoment = moment(newColumnId)
    const oldColumnMoment = moment(oldColumnId)


    if (newColumnMoment.isValid() && oldColumnMoment.isValid()) {
        offsetColumn = newColumnMoment.diff(oldColumnMoment, 'days')
    } else {
        offsetColumn = newColumnId - oldColumnId
    }

    offsetIndex = newIndexId - oldIndexId

    // adjust their column and index by adding offset old + offset = new
    const adjustedSelectedTagList = Object.keys(selectedTags).map(tagId => {
        const tag = selectedTags[tagId]

        let newColumn = -1


        if (pathName === '/phasePlan') {
            newColumn = parseInt(tag.position.column, 10) + parseInt(offsetColumn, 10)
        }

        if (pathName === '/lookahead') {

            newColumn = moment(tag.position.column).add(offsetColumn, 'days').valueOf()

        }
        const newRow = parseInt(tag.position.row, 10) + parseInt(offsetIndex, 10)

        return {
            column: newColumn,
            row: newRow
        }
    })

    // Check if any of the new are equal to the dropzone column and index if so return true
    const foundDropZone = adjustedSelectedTagList.filter(tag => tag.column === dropZoneColumn && tag.row === dropZoneIndex)

    const isPending = !!foundDropZone && foundDropZone.length > 0


    return isPending
})

export const isPendingLookaheadDropZone = createDeepEqualSelector(
    [
        getDraggedId,
        getSelectedNewPoints,
        getDropZoneColumn,
        getDropZoneIndex,
    ],
    (
        draggedId: string,
        selectedNewPoints: Array<Point>,
        dropZoneColumn: number,
        dropZoneIndex: number,
    ) => {
        if (!draggedId) return false
        if (!selectedNewPoints) return false

        const foundDropZone = selectedNewPoints.filter(tag => tag.column === dropZoneColumn && tag.row === dropZoneIndex)

        const isPending = !!foundDropZone && foundDropZone.length > 0

        return isPending;
    }
);

export const isPendingDropZone = createDeepEqualSelector(
    [
        getDraggedId,
        getDraggedOldPoint,
        getDraggedNewPoint,
        getDropZoneColumn,
        getDropZoneIndex,
        getLocationPathName,
        getSelectedPhasePlanTags,
        getSelectedLookaheadTags

    ],
    (
        draggedId: string,
        draggedOldPoint: Point,
        draggedNewPoint: Point,
        dropZoneColumn: number,
        dropZoneIndex: number,
        pathName: string,
        phaseTags,
        lookaheadTags
    ) => {
        // get the dragged tag
        if (!draggedId) return false


        //Get the dragged tag column and index


        const newColumnId = draggedNewPoint.column
        const newIndexId = draggedNewPoint.row

        const oldColumnId = draggedOldPoint.column
        const oldIndexId = draggedOldPoint.row



        if (!newIndexId) return false

        //Calculate the column offset and index offset new - old = offset
        let offsetColumn: number
        let offsetIndex: number

        const newColumnMoment = moment(newColumnId)
        const oldColumnMoment = moment(oldColumnId)


        if (pathName === '/lookahead') {
            offsetColumn = newColumnMoment.diff(oldColumnMoment, 'days')
        }

        if (pathName === '/phasePlan') {
            offsetColumn = newColumnId - oldColumnId
        }

        offsetIndex = newIndexId - oldIndexId


        // Get the select tags

        let selectedTags

        if (pathName === '/phasePlan') {

            selectedTags = { ...phaseTags }
        }

        if (pathName === '/lookahead') {

            selectedTags = { ...lookaheadTags }
        }

        if (!selectedTags) return false




        // adjust their column and index by adding offset old + offset = new
        const adjustedSelectedTagList = Object.keys(selectedTags).map(tagId => {
            const tag = selectedTags[tagId]

            let newColumn = -1


            if (pathName === '/phasePlan') {
                newColumn = parseInt(tag.position.column, 10) + parseInt(offsetColumn, 10)
            }

            if (pathName === '/lookahead') {

                newColumn = moment(tag.position.column).add(offsetColumn, 'days').valueOf()

            }
            const newRow = parseInt(tag.position.row, 10) + parseInt(offsetIndex, 10)

            return {
                column: newColumn,
                row: newRow
            }
        })

        // Check if any of the new are equal to the dropzone column and index if so return true

        const foundDropZone = adjustedSelectedTagList.filter(tag => tag.column === dropZoneColumn && tag.row === dropZoneIndex)

        const isPending = !!foundDropZone && foundDropZone.length > 0

        return isPending
    })

export const getMaxColumnFromTags = createDeepEqualSelector([getLookaheadColumnTags], (tags: Record<string, LookaheadTag>) => {
    if (!tags || Object.keys(tags).length <= 0) {
        return -1
    }

    const maxBasedOnColumn = Math.max.apply(null, Object.keys(tags).filter(column => column !== "undefined" && column !== "Invalid date").map(i => parseInt(moment(i, 'l').valueOf(), 10)))

    return maxBasedOnColumn
})

export const getMinColumnFromTags = createDeepEqualSelector([getLookaheadColumnTags], (tags: Record<string, LookaheadTag>) => {
    if (!tags || Object.keys(tags).filter(column => column !== "undefined" && column !== "Invalid date").length <= 0) {
        return -1
    }

    const minBasedOnColumn = Math.min.apply(null, Object.keys(tags).filter(column => column !== "undefined" && column !== "Invalid date").map(i => parseInt(moment(i, 'l').valueOf(), 10)))

    return minBasedOnColumn
})


export const getMaxBoardAppend = (state:any) => state.lookaheadReducer.maxBoardAppend
export const getMinBoardAppend = (state:any) => state.lookaheadReducer.minBoardAppend

export const getMinLookaheadDate = createSelector([getMinColumnFromTags, getMinBoardAppend], (minBasedOnColumn: number, minBoardAppend: number) => {
    if (minBasedOnColumn === -1) return moment().subtract(1, 'months').endOf('month').format('l')


    const minColumnPlusWeek = moment(minBasedOnColumn).subtract(2, 'months').subtract(minBoardAppend, 'months').startOf('month').format('l')

    return minColumnPlusWeek
})




export const getMaxLookaheadDate = createDeepEqualSelector([getMaxColumnFromTags, getDraggedNewPoint, getMaxBoardAppend], (maxBasedOnColumn: number, draggedPoint: Point, maxBoardAppend: number): string | undefined => {

    const draggedMax = !!draggedPoint && draggedPoint.column !== null ? draggedPoint.column : moment().valueOf()
    const maxColumn = Math.max(draggedMax, maxBasedOnColumn)

    const maxColumnPlusWeek = moment(maxColumn).add(2, 'months').add(maxBoardAppend*2, 'months').endOf('month').format('l')
    return maxColumnPlusWeek
})

const getMomentsFromHuddleFilter = (filter: HuddleFilter) => {

    if (filter === HuddleFilter.Daily) {
        return {
            start: moment().startOf('day'),
            end: moment().startOf('day')
        }
    }

    if (filter === HuddleFilter.Weekly) {
        return {
            start: moment().startOf('day'),
            end: moment().add(7, 'days').startOf('day')
        }
    }

    if (filter === HuddleFilter.All) {
        return {
            start: null,
            end: null
        }
    }

    return {
        start: null,
        end: null
    }
}



export const getDailyTag = createDeepEqualSelector([getLookaheadTagsList, getCurrentUserId, getLookaheadTradesList, getHuddleDate], (tagList: LookaheadTag[], currentUserId: string, tradeList: Trade[], huddleDate: Moment) => {
    const _tradeSet = tradeList
    .filter(trade => !!trade.users && !!trade.users[currentUserId]).map(trade => trade.uid)
    .filter((tradeId: string | undefined) => !!tradeId)
    .reduce((obj, tradeId: string) => {
        obj.add(tradeId)

        return obj
    }, new Set<string>())

    const tradeSet = _tradeSet as Set<string> 

    const filteredList = tagList
        .filter(tag => !!tradeSet.has(tag.tradeId))
        .filter(tag => tag.tagDate == huddleDate.format('l'))

    return filteredList
})

export const lookaheadTagFBConnect = (props: any) => {
    if (!props.projectId || !props.lookaheadId) {
        return [];
    }

    return [
        {
            path: `blueprints/projects/${props.projectId}/areas`,
            storeAs: 'SELECTED_LOOKAHEAD_AREAS'
        },
        {
            path: `projects/${props.projectId}/lookaheads/${props.lookaheadId}`,
            storeAs: 'SELECTED_LOOKAHEAD'
        },
        {
            path: `projects/${props.projectId}/lookaheads/${props.lookaheadId}/row_groups`,
            storeAs: 'SELECTED_LOOKAHEAD_ROW_GROUPS'
        },
        {
            path: `projects/${props.projectId}/lookaheads/${props.lookaheadId}/trades`,
            storeAs: 'SELECTED_LOOKAHEAD_TRADES'
        },
        {
            path: `projects/${props.projectId}/lookaheads/${props.lookaheadId}/tags`,
            storeAs: 'SELECTED_LOOKAHEAD_TAGS'
        },
        {
            path: `projects/${props.projectId}/lookaheads/${props.lookaheadId}/constraints`,
            storeAs: 'SELECTED_LOOKAHEAD_CONSTRAINT_TAGS'
        },
        {
            path: `projects/${props.projectId}/lookaheads/${props.lookaheadId}/milestone_tags/`,
            storeAs: 'SELECTED_LOOKAHEAD_MILESTONE_TAGS'
        },
        {
            path: `blueprints/projects/${props.projectId}/areas/`,
            storeAs: 'SELECTED_LOOKAHEAD_AREAS'
        }
    ];
};

export const isTagCommitted = createSelector([getTagUid, selectType, getActiveCommitment], (uid: string, type: ColumnType, commitment?: Commitment) => {
    if(!uid) {
        return
    }
    const tagId = uid
    if (type === ColumnType.PhasePlan || !commitment || !tagId || !commitment.commitLog) {
        return false
    }

    return !!commitment.commitLog[tagId]
})

export const getVarianceUpdates = (state: any): Record<string, VarianceTagChangeInterface> => state.lookaheadReducer.varianceUpdate

export const getMinCommitmentDate = createDeepEqualSelector([getLookaheadCommitmentList, getLookaheadTagsList], (commitmentList: Commitment[], tags: LookaheadTag[]): string | undefined => {
    let minDateTag: Moment = moment(0)

    tags.forEach(tag => {
        const tagDate = tag.tagDate

        if (!!tagDate) {
            const tagMoment = moment(tagDate, 'l')
            if (tagMoment.isBefore(minDateTag) || minDateTag.valueOf() === 0) {
                minDateTag = tagMoment
            }

        }
    })


    let minDateCommitment: Moment = moment(0)
    commitmentList.forEach(commitment => {
        const endDate = commitment.endDate

        if (!!endDate) {
            const endDateMoment = moment(endDate, 'l')
            if (endDateMoment.isBefore(minDateCommitment) || minDateCommitment.valueOf() === 0) {
                minDateCommitment = endDateMoment
            }
        }
    })

    if (minDateCommitment.valueOf() > 0) {
        return minDateCommitment.add(0, 'days').format('l')
    }

    if (minDateTag.valueOf() > 0) {
        return minDateTag.add(0, 'days').format('l')
    }

    return moment(0).format('l')
})

export const isCellHighlighted = createSelector([getDropZoneIndex, getDropZoneColumn, getSelectedRows, getSelectedColumns], (index: number, column: string, selectedRows: number[], selectedColumns: string[]): boolean => {
    const selectedRowsSet = new Set<number>(selectedRows);
    const selectedColumnsSet = new Set<string>(selectedColumns)

    if (selectedRowsSet.has(index)) {
        return true
    }

    if (selectedColumnsSet.has(column)) {
        return true
    }

    return false
})

export const getOldRowCountData = createSelector([rowCountRow, getRowGroups], (rowCountRow, rowGroups) => {
    let found = null;

    Object.keys(rowGroups).forEach(key => {
        const rowGroup = rowGroups[key]

        if (rowGroup.start === rowCountRow) {
            found = rowGroup
        }
    })

    return found
})

export const shouldColorBottomBorder = createDeepEqualSelector([getDropZoneIndex, getRowGroups, getLocalClosedRowGroups], (index, rowGroups, closedRowGroups) => {
    let shouldShow = false
    const closeGroupSet = new Set<number>(closedRowGroups)

    Object.keys(rowGroups).forEach(key => {
        const rowGroup = rowGroups[key]
        if (rowGroup.start - 1 === index && !closeGroupSet.has(rowGroup.start)) {
            shouldShow = true
        }

        if (closeGroupSet.has(index)) {
            shouldShow = false
        } else {
            if (rowGroup.end === index && !closeGroupSet.has(rowGroup.start)) {
                shouldShow = true
            }
        }
    })

    return shouldShow
})

export const getGroupColor = createDeepEqualSelector([getDropZoneIndex, getRowGroups, getClosedRowGroups], (index, rowGroups, closedRowGroups) => {
    let shouldShow = 'red'

    const closeGroupSet = new Set<number>(closedRowGroups)

    Object.keys(rowGroups).forEach(key => {
        const rowGroup = rowGroups[key]

        if (rowGroup.start - 1 === index) {

            shouldShow = !!rowGroup.color ? rowGroup.color : '#4286dd'
        }

        if (closeGroupSet.has(index)) {
            shouldShow = !!rowGroup.color ? rowGroup.color : '#4286dd'
        } else {
            if (rowGroup.end === index && !closeGroupSet.has(rowGroup.start)) {
                shouldShow = !!rowGroup.color ? rowGroup.color : '#4286dd'
            }
        }

    })

    return shouldShow
})

export type Bound = {
    start: string;
    end: string;
}

export const getGroupBounds = createDeepEqualSelector([getLookaheadTags, getRowGroups], (tags,  rowGroups) => {

    if(!tags) {
        return {}
    }

    const bounds = Object.keys(rowGroups).reduce((prev, cur) => {
        const rowGroup = rowGroups[cur]
        prev[rowGroup.start] = getBoundsFromRowGroup(tags, rowGroup)
        return prev
    }, {} as Record<number, Bound>)

    return bounds;
})

export const isInGroupBounds = createDeepEqualSelector([getDropZoneIndex, getDropZoneColumn, getLocalGroupBounds], (index, column, bounds) => {
    const columnMoment = moment(column)
    let found = false
    Object.keys(bounds).forEach(row => {
        if(row == index) {
            const bound = bounds[row as unknown as number]
            const startMoment = moment(bound.start, 'l')
            const endMoment = moment(bound.end, 'l')

            if (columnMoment.isSameOrAfter(startMoment) && columnMoment.isSameOrBefore(endMoment)) {
               
                found = true
            }
        }
    })

    return found
})

export const getRowGroupTag = createDeepEqualSelector([getDropZoneIndex, getRowGroups], (index: number, rowGroups) => {

    let foundRowGroup: RowGroup | null = null

    Object.keys(rowGroups).forEach(key => {
        const rowGroup = rowGroups[key]

        if (rowGroup.start === index) {
            foundRowGroup = {
                ...rowGroup,
                uid: key
            }
        }
    })

    if (!foundRowGroup) {
        throw new Error(`getRowGroupTag ${index} ${foundRowGroup}`)
    }


    return foundRowGroup
})

function getTagsByTradeId(tagsByTradeId: any, trades: Record<string, Trade>) {
    return Object.keys(tagsByTradeId)
        .filter((tradeId: string) => {
            return !!trades[tradeId];
        }).reduce((obj, tradeId: string) => {
            const tagsBy = tagsByTradeId[tradeId];

            const crewSize = _.sumBy(tagsBy, 'crewSize');

            obj[tradeId] = {
                name: trades[tradeId].name,
                crewSize
            };

            return obj;
        }, {} as Record<string, TradeAndCrew>);
}

function getBoundsFromRowGroup(tags: Record<string, LookaheadTag>, foundRowGroup: RowGroup) {
    let minDate = moment().add(30, 'years').format('l');
    let maxDate = moment(0).format('l');

    const filterTags = Object.keys(tags).filter(tagKey => {
        const tag = tags[tagKey];


        const tagDate = tag.tagDate;
        const uncheckedTagIndex = tag.indexId;

        if (!!tagDate && uncheckedTagIndex !== null) {
            const tagIndex = uncheckedTagIndex;

            if (tagIndex >= foundRowGroup.start && tagIndex <= foundRowGroup.end) {
                return true;
            }

        }
        return false;
    });

    filterTags.forEach(tagKey => {
        const tag = tags[tagKey];


        const tagDate = tag.tagDate;
        const uncheckedTagIndex = tag.indexId;

        if (!!tagDate && uncheckedTagIndex !== null) {

            const currentDate = moment(tag.tagDate, 'l');
            const minDateMoment = moment(minDate, 'l');
            const maxDateMoment = moment(maxDate, 'l');


            if (currentDate.valueOf() < minDateMoment.valueOf()) {
                minDate = tagDate;
            }

            if (currentDate.valueOf() > maxDateMoment.valueOf()) {
                maxDate = tagDate;
            }
        }
    });

    return {
        start: minDate,
        end: maxDate
    };
}




// WEBPACK FOOTER //
// ./src/selectors/lookahead.selector.ts